import * as Popover from '@radix-ui/react-popover';
import moment, { Moment } from 'moment';
import { useRef, useState } from 'react';

import { BUTTON_SIZES, GhostLinkButton } from 'ui/button';
import { ALIGNMENT } from 'ui/selects';
import { createDropdownToggle } from 'ui/selects/utils';
import { useDropdownPopoverAlignment } from 'ui/utility/hooks/useDropdownPopoverAlignment';
import { HORIZONTAL_ALIGNMENT, VERTICAL_ALIGNMENT } from 'ui/utility/types';
import { parseDate } from 'utils/datetime';
import noop from 'utils/noop';
import {
  DateAndCalendarWrapper,
  DatePickerWrapper,
  RowWrapper,
  StyledDatePickerInput,
  StyledSingleDateCalendar,
} from './DatePicker.styles';
import { DATE_PICKER_PORTAL_ID } from './constants';
import { DateInput } from './datePickerInput/DateInput';
import { NULL_DATE_PLACEHOLDER } from './helpers';
import { convertMomentToDateString, convertValidDateRange } from './momentToDateConversionHelpers';

export type DatePickerWithCollapsibleCalendarProps = {
  id?: string;
  /** Whether set date can be cleared */
  canClearDate?: boolean;
  /** Date to display in input and calendar */
  date: Moment | null;
  /** horizontal alignment of calender dropdown */
  horizontalAlignment?: ALIGNMENT.LEFT | ALIGNMENT.RIGHT;
  /** vertical alignment of calender dropdown */
  verticalAlignment?: ALIGNMENT.DOWN | ALIGNMENT.UP;
  /**
   * Automatically change the dropdown `verticalAlignment` and `horizontalAlignment`
   * to avoid collisions with the viewport.
   * When this is on, `verticalAlignment` and `horizontalAlignment` are used as default alignments.
   */
  automaticallyAvoidCollisions?: boolean;
  /** Function run when date is changed, either in date input or calendar */
  onDateChange?: (date: Moment | null) => void;
  /** Custom/override date placeholder text */
  placeholder?: string;
  /** Start and end time of a range of valid dates. Block days that aren't within the range */
  validDateRange?: {
    start?: Moment;
    end?: Moment;
  };
  /** Applies disabled styles for a disabled input */
  isDisabled?: boolean;
  /** Whether date is valid */
  hasError?: boolean;
  /** Whether input is read only */
  isReadOnly?: boolean;
  /** id passed through to the DateInput child prop */
  inputId?: string;
};

export const DatePickerWithCollapsibleCalendar = ({
  automaticallyAvoidCollisions = false,
  canClearDate = false,
  date,
  hasError = false,
  horizontalAlignment = ALIGNMENT.LEFT,
  id,
  inputId = 'date-input',
  isDisabled = false,
  onDateChange = noop,
  placeholder = NULL_DATE_PLACEHOLDER,
  validDateRange,
  verticalAlignment = ALIGNMENT.DOWN,
  ...props
}: DatePickerWithCollapsibleCalendarProps) => {
  const parentEl = useRef(null);
  const [currentDate, setCurrentDate] = useState<Moment | null>(date);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const toggleDropdown = createDropdownToggle(setIsCalendarOpen, isCalendarOpen);

  const handleSetDate = (newDate: Date | null) => {
    const dateToBeSet = newDate ? moment(newDate) : null;
    setCurrentDate(dateToBeSet);
    onDateChange(dateToBeSet);
    toggleDropdown(false);
  };

  const { popoverVerticalAlignment, popoverHorizontalAlignment } = useDropdownPopoverAlignment(
    horizontalAlignment as HORIZONTAL_ALIGNMENT,
    verticalAlignment as VERTICAL_ALIGNMENT,
  );

  return (
    <Popover.Root open={isCalendarOpen} onOpenChange={toggleDropdown}>
      <DateAndCalendarWrapper ref={parentEl} id={id} {...props}>
        <RowWrapper>
          <Popover.Trigger
            asChild
            onClick={(e) => {
              e.preventDefault();
              if (!isDisabled) {
                toggleDropdown();
              }
            }}
          >
            <DatePickerWrapper data-test="date-dropdown-trigger">
              <StyledDatePickerInput hasError={hasError}>
                <DateInput
                  date={currentDate}
                  id={inputId}
                  /* @ts-ignore */
                  isOptional
                  isReadOnly
                  isDisabled={isDisabled}
                  placeholder={placeholder}
                />
              </StyledDatePickerInput>
            </DatePickerWrapper>
          </Popover.Trigger>

          {canClearDate && currentDate && !isDisabled && (
            <GhostLinkButton onClick={() => handleSetDate(null)} size={BUTTON_SIZES.SMALL}>
              Clear
            </GhostLinkButton>
          )}
        </RowWrapper>
        <Popover.Portal>
          <Popover.Content
            asChild
            data-portal-id={DATE_PICKER_PORTAL_ID}
            hideWhenDetached // automatically hide the content when the trigger becomes fully occluded.
            avoidCollisions={automaticallyAvoidCollisions}
            side={popoverVerticalAlignment}
            align={popoverHorizontalAlignment}
            sideOffset={4} // space between the dropdown and the dropdown trigger
          >
            <StyledSingleDateCalendar
              date={currentDate ? convertMomentToDateString(currentDate) : parseDate()}
              onDateChange={(newDate) => handleSetDate(newDate)}
              validDateRange={convertValidDateRange(validDateRange)}
            />
          </Popover.Content>
        </Popover.Portal>
      </DateAndCalendarWrapper>
    </Popover.Root>
  );
};
